import { Injectable } from "@angular/core";
import { Store } from "@ngrx/store";
import * as fromApp from "../../store/app.reducer";
import * as DeliveryMethodAddressActions from "../../store/deliveryMethodAddress/deliveryMethodAddress.actions";
import * as _ from "lodash";
import {
  auditTime,
  distinctUntilChanged,
  filter,
  first,
  Subscription,
  takeWhile,
  timeout,
} from "rxjs";
import moment from "moment";
import { AlertController } from "@ionic/angular";
import { TranslateService } from "@ngx-translate/core";
import * as CartActions from "../../cart/store/cart.actions";
import { Router } from "@angular/router";
import * as selectors from "../../store/app.selectors";
import { DataStorageService } from "../data-storage/data-storage.service";
import * as CartActiveCouponsActions from "../../cart/cart-components/cart-active-coupons/store/cartActiveCoupons.actions";
import * as GroupAppActions from "../../store/group-app/groupApp.actions";
import * as AuthActions from "../../auth/store/auth.actions";
import { Capacitor } from "@capacitor/core";
import { AnalyticsServiceService } from "../analitycsService/analytics-service.service";
import { DineinTableLabelService } from "../dinein-table-label/dinein-table-label.service";

@Injectable({
  providedIn: "root",
})
export class DineinValidationsService {
  private dineInOrderStartTimestamp: any;
  private dineInOrderStopAlert: any;
  private intervalCheckingDineInStartTimestamp: any;

  private sub: Subscription;

  private dineInGuestReconcileSub: Subscription;

  private deferredGroupAppRestoreSub: Subscription;
  private deferredGroupAppRestoreArmed = false;
  private deferredGroupAppRestoreAttempts = 0;

  constructor(
    private store: Store<fromApp.AppState>,
    private alertController: AlertController,
    private dataStorageService: DataStorageService,
    private router: Router,
    private translateService: TranslateService,
    private analyticsService: AnalyticsServiceService,
    private dineinTableLabelService: DineinTableLabelService
  ) {}

  public publicStopDineInOrderStartTimestampChecks() {
    if (this.sub) {
      this.sub.unsubscribe();
      this.sub = null;
      this.clearDineInOrderStartTimestamp();
    }
    this.stopDineInGuestSessionLossChecks();
  }

  // In-session twin of LocalStorageDataService.reportDanglingDineInDeliveryMethod,
  // which only fires while the app is loading state. A guest session can also be
  // torn down mid-session with the table left behind: logoutErase keeps the
  // deliveryMethodAddress slice whenever do_not_navigate is set (deliberate, so a
  // guest logging out to use a normal account keeps their table), so the cart
  // keeps showing Dine In + the table with no only_dinein user and no way back.
  //
  // This probe never covered the late-ClearUser path that handleGroupAppForOnly-
  // DineinUser used to produce, and could not have: the check below requires both
  // user and mo_token to be absent, while that path left a live token behind.
  // That path is now guarded at the source, so nothing here is expected to change.
  //
  // OBSERVATION ONLY — deliberately. Repairing the state from here means clearing
  // a table out from under someone who may be mid-order, and review showed how
  // easily that goes wrong. Measure which of these paths actually fires in
  // production first, then decide what (if anything) should act on it.
  public subscribeToDineInGuestSessionLoss() {
    if (this.dineInGuestReconcileSub) {
      return;
    }

    this.dineInGuestReconcileSub = this.store
      .select("auth")
      .pipe(distinctUntilChanged())
      .pipe(auditTime(200))
      .subscribe((authState: any) => {
        this.checkDineInGuestSessionLoss(authState);
      });
  }

  private stopDineInGuestSessionLossChecks() {
    if (this.dineInGuestReconcileSub) {
      this.dineInGuestReconcileSub.unsubscribe();
      this.dineInGuestReconcileSub = null;
    }
  }

  // Kept local rather than imported from LocalStorageDataService: it is a pure
  // twelve-line function, and a cross-service import to reach it would couple two
  // root singletons for no benefit.
  private dineInGuestAgeBucket(ts) {
    if (!ts) {
      return "unknown";
    }
    const minutes = (Date.now() - ts) / 60000;
    if (minutes < 2) {
      return "lt2m";
    }
    if (minutes < 60) {
      return "lt1h";
    }
    return "gt1h";
  }

  private readDineInGuestMarker() {
    try {
      const raw = window.localStorage.getItem("mo_dinein_active");
      const parsed = raw ? JSON.parse(raw) : null;
      return parsed && parsed.table_id != null ? parsed : null;
    } catch (e) {
      return null;
    }
  }

  private checkDineInGuestSessionLoss(authState) {
    const marker = this.readDineInGuestMarker();
    if (!marker) {
      return;
    }

    // Both the user and the token must be gone. While the token is still in the
    // store the session is either live or mid-hydration (only_dinein arrives
    // with get-customer-data), and every login flow passes through that shape —
    // which is why this cannot key on the user alone, and needs no timer or
    // login-state heuristic to stay off legitimate flows. A do_not_navigate
    // logout does clear both, but it removes the marker as it goes.
    const sessionGone = !!(authState && !authState.user && !authState.mo_token);
    if (!sessionGone) {
      return;
    }

    // Same three outcomes as the boot probe, so the two are comparable. Note the
    // source differs by necessity: here the NgRx slice is authoritative, while at
    // boot only localStorage exists — a difference that matters because
    // clearLocalStorageMoToken() empties the persisted copy but not the store.
    let result = "no_delivery_method";
    let storeId = null;
    this.store
      .select("deliveryMethodAddress")
      .pipe(first())
      .subscribe((state: any) => {
        const table = state && state.dineIn ? state.selectedDineInTable : null;
        if (
          table &&
          table.table_id != null &&
          table.table_id.toString() === marker.table_id.toString()
        ) {
          result = "table_without_session";
        } else if (table) {
          result = "table_replaced";
        }
      })
      .unsubscribe();
    this.store
      .select("currentStore")
      .pipe(first())
      .subscribe((state: any) => {
        storeId = state && state.currentStoreId ? state.currentStoreId : null;
      })
      .unsubscribe();

    // The loss is established, so the marker goes either way — otherwise it
    // re-arms on every later auth emission and the same loss is counted twice.
    try {
      window.localStorage.removeItem("mo_dinein_active");
    } catch (e) {}

    // Reuses the dinein_auth_result id because the footprint consumer only keeps
    // custom fields for the five known dine-in ids. `source` carries the probe
    // and the marker's age, and separates these rows from real auth results —
    // existing queries over dinein_auth_result must filter on it. store_id is
    // passed explicitly: without it the footprint is silently dropped whenever
    // currentStoreId has not resolved. Nothing is dispatched — the cart is left
    // exactly as it was.
    this.analyticsService.dmAnalyticsCustomMessage("dinein_auth_result", {
      store_id: storeId,
      table_id: marker.table_id,
      source:
        "dangling_insession_" + this.dineInGuestAgeBucket(marker.ts),
      result: result,
      selected_table_found: result === "table_without_session",
      only_dinein_mode: true,
    });
  }

  getNoLocationDineInModalStatus() {
    return new Promise((resolve, reject) => {
      this.store
        .select("deliveryMethodAddress") // Assuming you select the state object
        .pipe(
          filter(
            (state) => state && state.noLocationDineInModalOpen !== undefined
          ), // Ensure state is valid
          first((state) => state.noLocationDineInModalOpen === "closed"), // Complete when the modal is closed
          timeout(4000) // Timeout after 3 seconds
        )
        .subscribe({
          next: (state) => {
            if (state && state.noLocationDineInModalOpen === "closed") {
              resolve({ status: "closed", dineIn: state.dineIn });
            } else {
              if (state && state.noLocationDineInModalOpen !== "opened") {
                resolve({ status: "closed", dineIn: state.dineIn });
              }
              if (state && state.noLocationDineInModalOpen === "opened") {
                resolve({ status: "opened", dineIn: state.dineIn });
              }
            }
            resolve(false);
          },
          error: (err) => {
            if (err.name === "TimeoutError") {
              resolve(false);
              // if (state && state.noLocationDineInModalOpen !== "opened") {
              //   resolve("closed");
              // }
            }
          },
        });
    });
  }

  public subscribeToDineInOrderStarTimestamp() {
    if (!this.sub) {
      this.sub = this.store
        .select("deliveryMethodAddress")
        .pipe(distinctUntilChanged())
        .pipe(auditTime(200))
        .subscribe((state) => {
          if (
            state &&
            !_.isEqual(
              state.dineInOrderStartTimestamp,
              this.dineInOrderStartTimestamp
            )
          ) {
            this.dineInOrderStartTimestamp = _.cloneDeep(
              state.dineInOrderStartTimestamp
            );

            if (this.dineInOrderStartTimestamp) {
              this.checkDineInOrderStartTimestamp(
                state.orderPickup,
                state.dineIn
              );
            }
          }

          if (state && (state.orderPickup || !state.dineIn)) {
            if (state.dineInOrderStartTimestamp) {
              this.clearDineInOrderStartTimestamp();
            }
            this.dismissDineInOrderStopAlert();
          }
        });
    }
  }

  private async checkDineInOrderStartTimestamp(orderPickup, dineIn) {
    const currentUser: any = await this.getCurrentUser();
    if (!dineIn || (currentUser && currentUser.uposMoUser)) {
      this.clearDineInOrderStartTimestamp();
    } else {
      if (this.dineInOrderStartTimestamp) {
        const orderStartTime = moment(
          parseInt(this.dineInOrderStartTimestamp.toString())
        );
        const currentTime = moment();

        if (
          currentTime.diff(orderStartTime, "hours") >= 1 &&
          currentTime.diff(orderStartTime, "hours") <= 4
        ) {
          console.log("More than 1 hour has passed since the order started.");

          const statusObject: any = await this.getNoLocationDineInModalStatus();

          console.log("statusObject", statusObject);

          if (
            statusObject &&
            statusObject.status &&
            statusObject.status === "closed" &&
            statusObject.dineIn
          ) {
            if (await this.refreshStrictWebOnlyActiveCartTimestamp(currentUser)) {
              return;
            }

            this.showDineInOrderStopAlert();
          }
        } else if (currentTime.diff(orderStartTime, "hours") > 4) {
          const statusObject: any = await this.getNoLocationDineInModalStatus();

          console.log("statusObject", statusObject);

          if (
            statusObject &&
            statusObject.status &&
            statusObject.status === "closed" &&
            statusObject.dineIn
          ) {
            await this.clearDineInAndStartOver(true);
          }
        } else {
          console.log("Less than 1 hour has passed since the order started.");
        }
      }
    }
  }

  async getCurrentUser() {
    return new Promise((resolve, reject) => {
      this.store
        .select("auth")
        .pipe(first())
        .subscribe((authState) => {
          if (authState && authState.user) {
            resolve(authState.user);
          } else {
            resolve(null);
          }
        })
        .unsubscribe();
    });
  }

  async getDeliveryMethodAddress() {
    return new Promise((resolve, reject) => {
      this.store
        .select("deliveryMethodAddress")
        .pipe(first())
        .subscribe((state) => {
          if (state) {
            resolve(state);
          } else {
            resolve(null);
          }
        })
        .unsubscribe();
    });
  }

  private async getCartProducts() {
    return new Promise((resolve, reject) => {
      this.store
        .select("cart")
        .pipe(first())
        .subscribe((cartState) => {
          resolve((cartState && cartState.products) || []);
        })
        .unsubscribe();
    });
  }

  private async getStoreInfoState() {
    return new Promise((resolve, reject) => {
      this.store
        .select(selectors.getStoreInfo)
        .pipe(first())
        .subscribe((storeInfo) => {
          resolve(storeInfo || null);
        })
        .unsubscribe();
    });
  }

  public async setDineInOrderStartTimestamp() {
    const currentUser: any = await this.getCurrentUser();

    const deliveryMethodAddress: any = await this.getDeliveryMethodAddress();

    if (currentUser && currentUser.uposMoUser) {
      this.clearDineInOrderStartTimestamp();
      return false;
    }

    const timestamp = Date.now().toString();
    this.store.dispatch(
      new DeliveryMethodAddressActions.setDineInOrderStartTimestamp(timestamp)
    );

    if (navigator.cookieEnabled) {
      let deliveryMethodAddress: any = window.localStorage.getItem(
        "deliveryMethodAddress"
      );

      if (deliveryMethodAddress) {
        deliveryMethodAddress = JSON.parse(deliveryMethodAddress);

        deliveryMethodAddress.dineInOrderStartTimestamp = timestamp;
        localStorage.setItem(
          "deliveryMethodAddress",
          JSON.stringify(deliveryMethodAddress)
        );
      }
    }

    if (this.intervalCheckingDineInStartTimestamp) {
      clearInterval(this.intervalCheckingDineInStartTimestamp);
      this.intervalCheckingDineInStartTimestamp = null;
    }

    this.intervalCheckingDineInStartTimestamp = setInterval(() => {
      this.checkDineInOrderStartTimestamp(
        deliveryMethodAddress.orderPickup,
        deliveryMethodAddress.dineIn
      );
    }, 3601000);
  }

  public clearDineInOrderStartTimestamp() {
    this.store.dispatch(
      new DeliveryMethodAddressActions.setDineInOrderStartTimestamp(null)
    );
    if (this.intervalCheckingDineInStartTimestamp) {
      clearInterval(this.intervalCheckingDineInStartTimestamp);
      this.intervalCheckingDineInStartTimestamp = null;
    }

    if (navigator.cookieEnabled) {
      let deliveryMethodAddress: any = window.localStorage.getItem(
        "deliveryMethodAddress"
      );

      if (deliveryMethodAddress) {
        deliveryMethodAddress = JSON.parse(deliveryMethodAddress);

        deliveryMethodAddress.dineInOrderStartTimestamp = null;
        localStorage.setItem(
          "deliveryMethodAddress",
          JSON.stringify(deliveryMethodAddress)
        );
      }
    }
  }

  public clearCart() {
    this.store
      .select("cart")
      .pipe(first())
      .subscribe((cartState) => {
        const cartProducts = cartState.products || [];

        this.store
          .select("cartActiveCoupons")
          .pipe(first())
          .subscribe((state) => {
            if (state && state.activeCoupons && state.activeCoupons.length) {
              const cartCoupons = _.cloneDeep(state.activeCoupons);
              _.each(cartProducts, (cartProduct) => {
                _.each(_.cloneDeep(cartCoupons), (coupon, index) => {
                  if (
                    coupon.couponCheckbox &&
                    coupon.product_id &&
                    cartProduct.product_id === coupon.product_id
                  ) {
                    cartCoupons[index].couponCheckbox = false;
                  }
                });
              });

              this.store.dispatch(
                new CartActiveCouponsActions.SetActiveCoupons(
                  _.cloneDeep(cartCoupons)
                )
              );
            }

            this.store.dispatch(new CartActions.ClearCart());
            if (navigator.cookieEnabled) {
              window.localStorage.removeItem("cart");
            }
          });
      })
      .unsubscribe();
  }

  public clearDeliveryMethod() {
    this.store.dispatch(
      new DeliveryMethodAddressActions.setSelectedAddress(_.cloneDeep(null))
    );
    this.store.dispatch(
      new DeliveryMethodAddressActions.setValidStores(_.cloneDeep(null))
    );
    this.store.dispatch(
      new DeliveryMethodAddressActions.setValidDeliveryAreas(_.cloneDeep(null))
    );
    this.store.dispatch(
      new DeliveryMethodAddressActions.setOpenNotValidAddressModal(false)
    );
    this.store.dispatch(
      new DeliveryMethodAddressActions.setOpenSelectStorePopupDelivery(false)
    );
    this.store.dispatch(new DeliveryMethodAddressActions.setDineIn(null));
    this.store.dispatch(new DeliveryMethodAddressActions.setOrderPickup(null));

    this.store.dispatch(
      new DeliveryMethodAddressActions.setCarTakeawayPlateNumber(null)
    );
    this.store.dispatch(
      new DeliveryMethodAddressActions.setCarTakewayComments(null)
    );

    this.store.dispatch(
      new DeliveryMethodAddressActions.setSelectedDineInTable(null)
    );

    if (navigator.cookieEnabled) {
      let deliveryMethodAddressObj: any = localStorage.getItem(
        "deliveryMethodAddress"
      );
      if (deliveryMethodAddressObj) {
        deliveryMethodAddressObj = JSON.parse(deliveryMethodAddressObj);
        deliveryMethodAddressObj.dineIn = null;
        deliveryMethodAddressObj.orderPickup = null;
        deliveryMethodAddressObj.selectedAddress = null;
        deliveryMethodAddressObj.selectedDineInTable = null;
        deliveryMethodAddressObj.carTakeAwayComments = null;
        deliveryMethodAddressObj.carTakeAwayPlateNumber = null;

        localStorage.setItem(
          "deliveryMethodAddress",
          JSON.stringify(deliveryMethodAddressObj)
        );
      }
    }
  }

  async getStoreInfo() {
    return new Promise((resolve, reject) => {
      this.store
        .select(selectors.getStoreInfo)
        .pipe(
          filter((storeInfo) => !!storeInfo),
          first()
        )
        .subscribe((storeInfo) => {
          resolve(storeInfo);
        });
    });
  }

  getDeviceId() {
    return new Promise((resolve) => {
      this.store
        .select("platformInfo")
        .pipe(first())
        .subscribe((state) => {
          resolve(state.device_id || null);
        });
    });
  }

  private async fetchDineInTables() {
    const storeInfo: any = await this.getStoreInfo();
    const device_id = await this.getDeviceId();
    return new Promise((resolve) => {
      this.dataStorageService
        .getDineInTables(storeInfo.group, storeInfo.store_id, device_id)
        .subscribe({
          next: async (res: any) => {
            if (!res || !res.success) {
              resolve("error");
            } else {
              resolve(res.tables);
            }
          },
          error: async (error) => {
            resolve("error");
          },
        });
    });
  }

  public async dismissDineInOrderStopAlert() {
    if (this.dineInOrderStopAlert && this.dineInOrderStopAlert !== true) {
      await this.dineInOrderStopAlert.dismiss();
      this.dineInOrderStopAlert = null;
    } else if (this.dineInOrderStopAlert === true) {
      setTimeout(async () => {
        await this.dineInOrderStopAlert.dismiss();
        this.dineInOrderStopAlert = null;
      }, 100);
    }
  }

  private async showDineInOrderStopAlert() {
    if (!this.dineInOrderStopAlert) {
      this.dineInOrderStopAlert = true;
      const deliveryMethodAddressState: any =
        await this.getDeliveryMethodAddress();

      const dineInTable: any = deliveryMethodAddressState.selectedDineInTable;
      const allDineInTables: any = await this.fetchDineInTables();

      let uniqueDineInTableStore: boolean = false;
      if (
        allDineInTables &&
        _.isArray(allDineInTables) &&
        allDineInTables.length &&
        allDineInTables.length === 1
      ) {
        uniqueDineInTableStore = true;
      }

      this.dineInOrderStopAlert = await this.alertController.create({
        header: this.translateService.instant("on-going-order"),
        message: uniqueDineInTableStore
          ? this.translateService.instant(
              "do-you-want-to-continue-your-current-dine-in-order"
            )
          : this.dineinTableLabelService.instant(
              "do-you-want-to-continue-your-current-dine-in-order-at-table"
            ) +
            " " +
            this.translateService.instant(dineInTable.name) +
            "?",
        backdropDismiss: false,
        buttons: [
          {
            text: this.translateService.instant("new-order"),
            cssClass: "secondary",
            handler: async () => {
              await this.clearDineInAndStartOver();
            },
          },
          {
            text: this.translateService.instant("continue"),
            role: "cancel",
            handler: () => {},
          },
        ],
      });

      await this.dineInOrderStopAlert.present();
      await this.dineInOrderStopAlert.onDidDismiss().then((data) => {
        this.dineInOrderStopAlert = null;
      });
    }
  }

  async clearDineInAndStartOver(preserveStrictWebOnlyActiveCart = false) {
    const user: any = await this.getCurrentUser();
    if (
      preserveStrictWebOnlyActiveCart &&
      (await this.refreshStrictWebOnlyActiveCartTimestamp(user))
    ) {
      return;
    }

    this.clearDeliveryMethod();
    this.clearCart();

    if (
      (!user || !user.only_dinein) &&
      this.router &&
      this.router.url &&
      !this.router.url.includes("home")
    ) {
      this.router.navigateByUrl("/homepage", {
        replaceUrl: true,
      });
    } else if (user && user.only_dinein) {
      this.store.dispatch(
        new AuthActions.SetLogoutFlagTimestamp(Date.now().toString())
      );
      if (this.router && this.router.url && !this.router.url.includes("home")) {
        this.router.navigateByUrl("/homepage", {
          replaceUrl: true,
        });
      }
    }
  }

  private async shouldPreserveStrictWebOnlyActiveCart(user) {
    if (
      !user ||
      !user.only_dinein ||
      user.uposMoUser ||
      Capacitor.isNativePlatform()
    ) {
      return false;
    }

    const storeInfo: any = await this.getStoreInfoState();
    if (!this.isStrictWebOnlyDineInStore(storeInfo)) {
      return false;
    }

    const deliveryMethodAddress: any = await this.getDeliveryMethodAddress();
    if (
      !deliveryMethodAddress ||
      !deliveryMethodAddress.dineIn ||
      !deliveryMethodAddress.selectedDineInTable
    ) {
      return false;
    }

    const cartProducts: any = await this.getCartProducts();
    return !!(cartProducts && cartProducts.length);
  }

  private async refreshStrictWebOnlyActiveCartTimestamp(user) {
    if (!(await this.shouldPreserveStrictWebOnlyActiveCart(user))) {
      return false;
    }

    await this.setDineInOrderStartTimestamp();
    return true;
  }

  private isStrictWebOnlyDineInStore(storeInfo) {
    return !!(
      storeInfo &&
      storeInfo.dinein &&
      storeInfo.dine_in_disable_table_selection &&
      storeInfo.only_visitors_app &&
      !storeInfo.onlyAppsDineIn &&
      !Capacitor.isNativePlatform()
    );
  }

  // One-line seam. The error path's reload decision is the most destructive
  // branch in this file and has already shipped two defects, but a browser will
  // not let a test stub location.reload, so the call is wrapped where a spy can
  // reach it. Nothing else may go in here.
  protected reloadApp() {
    location.reload();
  }

  // The error path's suppressed reload leaves a debt. That fetch is the one that
  // just failed, and a full boot is the only other thing that can put a fresh
  // group-app on the slice: the ten other SetGroupApp writers all clone the live
  // one, and the slice is not persisted. So skipping the reload for an in-flight
  // login would leave pointsSystem/affiliateActive off for the rest of the
  // session unless the restore is re-armed for the moment that login resolves.
  // The guard is evaluated once, ~30s after the logout, and an SMS round trip
  // routinely finishes after that — so without this, the common outcome of the
  // window it protects is the very state it was added to prevent.
  //
  // It pays the debt by RE-FETCHING, not by reloading later. A reload here would
  // land at the worst possible instant: logoutErase("do_not_navigate") keeps the
  // dine-in table in the store but removes its localStorage copy, and the only
  // boot restorer reads that copy, so a boot anywhere in this window costs the
  // customer their table. save_mo_token writes the token before dispatching it,
  // so the wait below fires while the table is still store-only. Re-fetching
  // restores the configuration without booting anything.
  //
  // It waits for a TOKEN, not for newCustomer to clear, because newCustomer is
  // never torn down on success. Any token will do: what the wait needs is a
  // session whose configuration is worth restoring, and the strip below keys on
  // the user, not on which flow produced the token.
  //
  // skipToken excludes the session whose attempt just failed. Without it a
  // re-arm would re-enter immediately and synchronously: the store is
  // BehaviorSubject-backed, so a fresh subscription replays the same token the
  // attempt was made for. attempts caps the whole thing regardless.
  private armDeferredGroupAppRestore(group, skipToken = null) {
    if (this.deferredGroupAppRestoreArmed) {
      return;
    }
    if (this.deferredGroupAppRestoreAttempts >= 3) {
      return;
    }
    this.deferredGroupAppRestoreArmed = true;
    if (this.deferredGroupAppRestoreSub) {
      this.deferredGroupAppRestoreSub.unsubscribe();
    }
    this.deferredGroupAppRestoreSub = this.store
      .select("auth")
      .pipe(
        filter(
          (state: any) =>
            !!(state && state.mo_token && state.mo_token !== skipToken)
        ),
        first()
      )
      .subscribe((state: any) => {
        this.deferredGroupAppRestoreArmed = false;
        this.deferredGroupAppRestoreSub = null;
        this.deferredGroupAppRestoreAttempts =
          this.deferredGroupAppRestoreAttempts + 1;
        this.restoreGroupAppFromServer(group, state.mo_token);
      });
  }

  // Restores ONLY what the strip took: pointsSystem and affiliateActive. The
  // sibling success callback dispatches the server body whole, but it is running
  // in the logout that just tore the session down; this one fires later, into a
  // live app, and a whole-slice replace there is a much bigger act than the debt
  // it is paying. The server copy has none of the boot transforms
  // (normalizeGroupAppDesignSettings, trustHomepageCustomButtonsHTML), none of
  // the language logos merged onto the live slice, and no customPages, so
  // dispatching it whole would revert them and lean on the groupApp subscription
  // to put each one back. Merging the two fields is the same lesson as the
  // language-logo fix in this PR: write the keys you own, leave the rest.
  //
  // A guest owning the store means the stripped config is the correct one, so
  // nothing is written — and the wait is re-armed rather than spent, because the
  // session that actually needs the restore may still be coming.
  //
  // A failed attempt must not spend the wait either: a guest re-claiming the
  // table mid-OTP dispatches SetMoToken too, and if the endpoint is still down
  // when it does, the session that needed the restore is the next one. Re-arming
  // excludes the token just tried, so the BehaviorSubject's replay cannot
  // re-enter this synchronously, and stops after three attempts.
  private restoreGroupAppFromServer(group, token) {
    this.dataStorageService.fetchGroupApp(group).subscribe({
      next: (res: any) => {
        // A falsy body restores nothing, exactly as the boot path and the
        // success callback both guard against.
        if (!res) {
          this.armDeferredGroupAppRestore(group, token);
          return;
        }

        const authNow: any = this.readAuthState();
        const liveUser = authNow ? authNow.user : null;
        if (
          liveUser &&
          (liveUser.only_dinein || liveUser.only_visitor || liveUser.uposMoUser)
        ) {
          this.armDeferredGroupAppRestore(group, token);
          return;
        }

        this.store
          .select("groupApp")
          .pipe(first())
          .subscribe((state: any) => {
            if (!state || !state.groupApp) {
              return;
            }
            const updatedGroupApp = _.cloneDeep(state.groupApp);
            updatedGroupApp.pointsSystem = res.pointsSystem;
            updatedGroupApp.affiliateActive = res.affiliateActive;
            if (_.isEqual(updatedGroupApp, state.groupApp)) {
              return;
            }
            this.store.dispatch(
              new GroupAppActions.SetGroupApp(_.cloneDeep(updatedGroupApp))
            );
          })
          .unsubscribe();
      },
      error: () => {
        this.armDeferredGroupAppRestore(group, token);
      },
    });
  }

  // Synchronous one-shot read of the auth slice, for use inside a late callback
  // that must not trust the state it closed over. NgRx Store is BehaviorSubject-
  // backed, so select + first() emits in the same tick and the subscription is
  // already complete by the time .unsubscribe() runs — the same idiom the
  // dangling-table probe uses above. Returns the raw slice (never a Promise) so
  // the caller stays synchronous and cannot introduce a second race.
  private readAuthState() {
    let authState: any = null;
    this.store
      .select("auth")
      .pipe(first())
      .subscribe((state: any) => {
        authState = state;
      })
      .unsubscribe();
    return authState;
  }

  public handleGroupAppForOnlyDineinUser(action, currentUser, do_not_navigate) {
    if (action === "login") {
      this.store
        .select("groupApp")
        .pipe(first())
        .subscribe((state) => {
          if (
            state &&
            state.groupApp &&
            (currentUser.only_dinein || currentUser.only_visitor)
          ) {
            const groupApp = _.cloneDeep(state.groupApp);

            if (groupApp.pointsSystem) {
              groupApp.pointsSystem = false;
            }

            if (groupApp.affiliateActive) {
              groupApp.affiliateActive = false;
            }

            this.store.dispatch(
              new GroupAppActions.SetGroupApp(_.cloneDeep(groupApp))
            );
          }
        })
        .unsubscribe();
    } else if (action === "logout") {
      this.store
        .select("groupApp")
        .pipe(first())
        .subscribe((state) => {
          if (
            state &&
            state.groupApp &&
            currentUser &&
            (currentUser.only_dinein || currentUser.only_visitor)
          ) {
            this.dataStorageService
              .fetchGroupApp(state.groupApp.group)
              .subscribe({
                next: (res: any) => {
                  // fetchGroupApp is timeout(10000) + retry(2), so this callback
                  // can land ~30s after the logout that started it — long after a
                  // replacement guest session exists (a dine-in table switch
                  // logs out and re-authenticates in ~1-3.5s). Re-read auth
                  // instead of trusting the closure.
                  //
                  // Keyed on mo_token, not user: a session-creation path writes
                  // the token before the user is hydrated (dine-in-modal
                  // dispatches SetMoToken on the auth response and the user only
                  // lands when get-customer-data returns), so a user-keyed guard
                  // is blind for exactly that window. A live token therefore
                  // means a NEWER session owns the store, and clearing its user
                  // or navigating it away is cross-session damage.
                  //
                  // When the token is gone the logout still stands, so ClearUser
                  // is dispatched as before. That also keeps an existing safety
                  // net: getUserData is never cancelled on logout and dispatches
                  // SetUser with no session check, so an in-flight one can
                  // resurrect the user after logoutErase already cleared it.
                  //
                  // A live token here can therefore only belong to a newer
                  // session: logoutErase clears the token synchronously before
                  // any callback can run — app.component.ts dispatches
                  // ClearMoToken in the same straight-line block that starts this
                  // fetch, with no await, early return or try/catch between them.
                  // (A throw in a subscribe callback cannot break that block
                  // either: rxjs 7 reports it asynchronously and the caller
                  // continues.)
                  //
                  // A login that has not produced a token YET is invisible to
                  // that check, and the OTP upgrade a dine-in guest starts right
                  // after logging out sits in exactly that window.
                  // auth.newCustomer is the flow's marker: the login/OTP popups
                  // write it and logoutErase clears it, so a value here was
                  // written after this logout began.
                  //
                  // It must be paired with the ABSENCE of a token, because
                  // newCustomer is not torn down on success: the only
                  // ClearNewCustomer outside logoutErase sits in the "resend"
                  // branch of the magic-login popups, which re-dispatches
                  // SetNewCustomer on the next line, and mo_socket_login
                  // re-writes it with the password nulled. On its own it would
                  // latch true for the rest of the session and permanently
                  // suppress the error-path reload — the only restorer that
                  // path has. With the token absent it means what it says: a
                  // login exists but has not produced a session yet. Once a
                  // token appears, the token checks take over.
                  //
                  // It gates the two destructive acts only — yanking the user to
                  // /homepage, and the error-path reload. ClearUser stays keyed
                  // on the token alone, so the resurrection safety net above is
                  // not weakened.
                  const authNow: any = this.readAuthState();
                  const newerSession = !!(authNow && authNow.mo_token);
                  const loginInFlight = !!(
                    authNow &&
                    authNow.newCustomer &&
                    !authNow.mo_token
                  );

                  if (!newerSession) {
                    this.store.dispatch(new AuthActions.ClearUser());
                  }

                  // A falsy body would null the whole groupApp slice, since
                  // SET_GROUPAPP replaces it wholesale. The boot path guards the
                  // same way before dispatching.
                  if (res) {
                    const groupApp = _.cloneDeep(res);
                    // Only a session we did NOT just clear can still own the
                    // store. authNow is a pre-ClearUser snapshot, so reading its
                    // user on the !newerSession path would strip the config for
                    // the very user the dispatch above deleted — which is
                    // reachable, since an in-flight getUserData can resurrect
                    // that user with no token behind it.
                    const liveUser =
                      newerSession && authNow ? authNow.user : null;
                    if (
                      liveUser &&
                      (liveUser.only_dinein ||
                        liveUser.only_visitor ||
                        liveUser.uposMoUser)
                    ) {
                      // A guest owns the store now, so the restored copy must not
                      // hand back what handleGroupAppForOnlyDineinUser("login")
                      // or handleGroupAppForUposUser("login") took away. Those
                      // branches run off a user change and will not re-run for
                      // the groupApp change dispatched below.
                      if (groupApp.pointsSystem) {
                        groupApp.pointsSystem = false;
                      }
                      if (groupApp.affiliateActive) {
                        groupApp.affiliateActive = false;
                      }
                    }
                    this.store.dispatch(
                      new GroupAppActions.SetGroupApp(_.cloneDeep(groupApp))
                    );
                  }

                  if (!do_not_navigate && !newerSession && !loginInFlight) {
                    this.router.navigateByUrl("/homepage", {
                      replaceUrl: true,
                    });
                  }
                },
                error: (err) => {
                  // Same staleness rule, and this one is the more destructive of
                  // the two: only_dinein/only_visitor tokens are deliberately
                  // never persisted (clearLocalStorageMoToken), so a reload
                  // destroys a live guest session outright while the cart is
                  // restored from localStorage — which is the exact "cart full,
                  // table selected, nobody logged in" symptom this fixes.
                  //
                  // Skipped only for a session the reload would actually
                  // destroy. A reload is this path's ONLY restorer — the fetch
                  // that was meant to un-strip the config is the one that just
                  // failed — so suppressing it for a session that survives a
                  // reload would leave pointsSystem/affiliateActive off with
                  // nothing to turn them back on. Persistence, not the user
                  // flags, is the test: it is what a reload actually consults,
                  // and unlike auth.user it is already correct during the
                  // hydration window this guard exists to cover. save_mo_token
                  // writes localStorage before dispatching, so a normal account
                  // is never briefly mistaken for a guest.
                  //
                  // Same newCustomer rule as the next callback, token-absence
                  // included: a reload during an in-flight OTP upgrade throws
                  // away the half-finished login as surely as it throws away a
                  // guest token. Once that login HAS a token, this must fall
                  // back to the persistence test above, or a logged-in normal
                  // account keeps a stripped config with nothing left to restore
                  // it. That token can also arrive AFTER this callback has
                  // already run and skipped the reload, which is the ordinary
                  // case rather than the exotic one — the fetch gives up ~30s in
                  // and an SMS round trip is slower than that — so the skip arms
                  // armDeferredGroupAppRestore instead of simply returning. That
                  // pays the debt with a re-fetch rather than a later reload,
                  // which would cost the table this whole window protects.
                  const authNow: any = this.readAuthState();
                  const storedMoToken = navigator.cookieEnabled
                    ? window.localStorage.getItem("mo_token")
                    : null;
                  const reloadWouldKillSession = !!(
                    authNow &&
                    authNow.mo_token &&
                    storedMoToken !== authNow.mo_token
                  );
                  const loginInFlight = !!(
                    authNow &&
                    authNow.newCustomer &&
                    !authNow.mo_token
                  );

                  if (reloadWouldKillSession) {
                    // #409's rule, unchanged: the reload would destroy the very
                    // session it means to help, and that session is a guest,
                    // whose stripped config is the correct one. Nothing is owed.
                    return;
                  }

                  if (loginInFlight) {
                    // Suppressing here only DEFERS the restore; it does not
                    // cancel it. Without the re-arm the login this protects
                    // keeps a stripped config once it succeeds.
                    this.armDeferredGroupAppRestore(state.groupApp.group);
                    return;
                  }

                  this.reloadApp();
                },
              });
          } else if (!currentUser && !do_not_navigate) {
            // currentUser was already cleared (e.g. by the visitor
            // terminal-cleanup do_not_navigate logout that fires 3s after a
            // complete/canceled order) but a navigating restart was requested
            // (the success-lottie OK). auth.user is already null here, so the
            // OnlyDineInGuard lets /homepage through. This fires only when
            // currentUser is null; normal/dinein/visitor logouts with a present
            // user keep using the branch above, so the working paths and the
            // do_not_navigate terminal cleanup are untouched.
            this.router.navigateByUrl("/homepage", {
              replaceUrl: true,
            });
          }
        });
    }
  }
}
